import { Box, Typography, Link, Chip, Card, CardContent } from "@mui/material";
import Header from "../components/header";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import WorkIcon from "@mui/icons-material/Work";
import SchoolIcon from "@mui/icons-material/School";
import VerifiedIcon from "@mui/icons-material/Verified";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useState } from "react";

// Hardcoded data
const experienceData = {
  coreCompetencies: [
    "Product Roadmapping & Backlog Prioritization",
    "Requirements Gathering & User Stories",
    "Stakeholder & Cross-Functional Collaboration",
    "Agile Scrum & Kanban Practices",
    "Data-Driven Decision Making & Metrics Tracking",
    "API & System Design",
    "Customer-Centric Solution Development"
  ],
  experience: [
    {
      company: "Alpha Omega - FDIC",
      position: "Software Developer",
      years: "Jul 2023 - Present",
      details: [
        "Partnered with product managers and stakeholders to define requirements for the new BankFind Suite, enhancing accessibility for public users",
        "Coordinated migration of FDIC APIs to api.data.gov, improving API security, usage monitoring, and developer experience",
        "Worked within Agile Scrum processes, breaking down roadmap initiatives into deliverable tasks and ensuring alignment with business objectives",
        "Delivered end-to-end product features across frontend (React) and backend (Node.js, Elasticsearch), balancing technical feasibility with user needs",
        "Monitored adoption metrics and implemented logging to track data indexing processes, ensuring transparency and reliability of public data",
        "Supported release readiness through code reviews, documentation, and cross-team collaboration"
      ],
      summary: "Partnered with product managers to define requirements and deliver features for the BankFind Suite, a public-facing financial data portal."
    },
    {
      company: "OtherWhy LLC",
      position: "Software Developer",
      years: "Mar 2022 - Jul 2023",
      details: [
        "Collaborated with product managers, designers, and business stakeholders to define scope for new web and mobile applications",
        "Led early-stage product scaffolding, establishing technical foundations while ensuring scalability aligned with long-term product vision",
        "Partnered with architects and technical PMs to implement cloud infrastructure (AWS, GraphQL, Cognito) supporting user authentication and content delivery",
        "Supported go-to-market readiness by building user-facing login and registration flows and aligning UX with customer feedback",
        "Advocated for customer needs through cross-team collaboration in Agile ceremonies (standups, retros, grooming)"
      ],
      summary: "Collaborated with product managers to define scope and deliver scalable web and mobile applications with cloud infrastructure."
    },
    {
      company: "Advanced Systems Concepts, Inc.",
      position: "Sales Development Representative",
      years: "Mar 2020 - May 2021",
      details: [
        "Helped pioneer the SDR team for ActiveBatch workload automation software",
        "Identified customer needs and provided insights to product and sales teams to inform demos, discovery calls, and product positioning",
        "Worked cross-functionally with marketing and sales leadership to optimize prospecting processes and expand market reach",
        "Led executive user discovery by recruiting and screening C-suite leaders at tech companies to uncover workload automation needs"
      ],
      summary: "Pioneered SDR team and identified customer needs to inform product positioning and expand market reach for ActiveBatch."
    }
  ],
  education: [
    {
      institution: "Rowan University",
      degree: "Bachelor of Science",
      discipline: "Psychology",
      graduated: "2019",
      details: [
        "Conducted research to determine correlations between academic stress, parental pressure, procrastination, and stimulant use in undergraduate students",
        "Using Latent Class Analysis and self-report questionnaires from over two-hundred students, research concluded that procrastination, parental pressure, and academic stress were possible predictors of licit stimulant use"
      ]
    }
  ],
  certifications: [
    {
      institution: "SkillUp",
      name: "Product Management: Foundations & Stakeholder Collaboration",
      link: "https://www.coursera.org/account/accomplishments/verify/TZHYAKIY5FCE"
    },
    {
      institution: "Microsoft",
      name: "Business Analysis Fundamentals",
      link: "https://www.coursera.org/account/accomplishments/verify/M840Y94K55E7"
    },
    {
      institution: "IBM",
      name: "Product Management: An Introduction",
      link: "https://www.coursera.org/account/accomplishments/verify/0L8B6UUOD4JX"
    },
    {
      institution: "FreeCodeCamp",
      name: "Scientific Computing with Python",
      link: "https://www.freecodecamp.org/certification/brandmch/scientific-computing-with-python-v7"
    },
    {
      institution: "FreeCodeCamp",
      name: "Back End Development and APIs",
      link: "https://www.freecodecamp.org/certification/brandmch/back-end-development-and-apis"
    },
    {
      institution: "FreeCodeCamp",
      name: "Front End Development Libraries",
      link: "https://www.freecodecamp.org/certification/brandmch/front-end-development-libraries"
    },
    {
      institution: "FreeCodeCamp",
      name: "Responsive Web Design",
      link: "https://www.freecodecamp.org/certification/brandmch/responsive-web-design"
    },
    {
      institution: "FreeCodeCamp",
      name: "JavaScript Algorithms and Data Structures",
      link: "https://www.freecodecamp.org/certification/brandmch/javascript-algorithms-and-data-structures"
    }
  ]
};


const JobBox = ({ job }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card
      onClick={() => setExpanded(!expanded)}
      sx={{
        mb: 3,
        borderRadius: 3,
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
        cursor: "pointer",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)"
        }
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={2}>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontFamily: "Roboto Slab",
                fontWeight: 700,
                color: "#333",
                mb: 0.5
              }}
            >
              {job.company}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Roboto",
                color: "#10b981",
                fontWeight: 600
              }}
            >
              {job.position}
            </Typography>
          </Box>
          <Chip
            label={job.years}
            sx={{
              backgroundColor: "#ecfdf5",
              color: "#10b981",
              fontWeight: 600,
              fontFamily: "Roboto"
            }}
          />
        </Box>

        {expanded ? (
          <Box>
            {job.details.map((detail, index) => (
              <Box key={index} display="flex" mb={1.5}>
                <Typography sx={{ color: "#10b981", mr: 1.5, fontWeight: 600 }}>•</Typography>
                <Typography sx={{ fontFamily: "Roboto", color: "#555", lineHeight: 1.7 }}>
                  {detail}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography sx={{ fontFamily: "Roboto", color: "#666", lineHeight: 1.7 }}>
            {job.summary}
          </Typography>
        )}

        <Typography
          sx={{
            mt: 2,
            fontFamily: "Roboto",
            fontSize: "0.875rem",
            color: "#999",
            fontStyle: "italic"
          }}
        >
          {expanded ? "Click to collapse" : "Click to expand"}
        </Typography>
      </CardContent>
    </Card>
  );
};

const EducationBox = ({ edu }) => {
  return (
    <Card
      sx={{
        mb: 3,
        borderRadius: 3,
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
        transition: "transform 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)"
        }
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={2}>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontFamily: "Roboto Slab",
                fontWeight: 700,
                color: "#333",
                mb: 0.5
              }}
            >
              {edu.institution}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Roboto",
                color: "#10b981",
                fontWeight: 600
              }}
            >
              {edu.degree} - {edu.discipline}
            </Typography>
          </Box>
          <Chip
            label={`Graduated ${edu.graduated}`}
            sx={{
              backgroundColor: "#ecfdf5",
              color: "#10b981",
              fontWeight: 600
            }}
          />
        </Box>

        {edu.details.map((detail, index) => (
          <Box key={index} display="flex" mb={1.5}>
            <Typography sx={{ color: "#10b981", mr: 1.5, fontWeight: 600 }}>•</Typography>
            <Typography sx={{ fontFamily: "Roboto", color: "#555", lineHeight: 1.7 }}>
              {detail}
            </Typography>
          </Box>
        ))}
      </CardContent>
    </Card>
  );
};


function Experience() {
  return (
    <Box sx={{ backgroundColor: "#f5f7fa", minHeight: "100vh" }}>
      <Header />

      {/* Professional Summary Section */}
      <Box sx={{ py: 8, px: 3, backgroundColor: "#f5f7fa" }}>
        <Box sx={{ maxWidth: "1200px", margin: "0 auto" }}>
          <Card 
            sx={{ 
              borderRadius: 3, 
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              color: "white"
            }}
          >
            <CardContent sx={{ p: 5 }}>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: "Roboto Slab",
                  fontWeight: 700,
                  mb: 3,
                  textAlign: "center"
                }}
              >
                Professional Summary
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontSize: "1.15rem",
                  lineHeight: 1.8,
                  textAlign: "center",
                  maxWidth: "900px",
                  margin: "0 auto",
                  opacity: 0.95
                }}
              >
                Product-focused professional with a strong foundation in software engineering and customer engagement. Experienced in
                defining requirements, collaborating with cross-functional teams, and delivering solutions that improve user experience and
                business outcomes. Skilled at bridging the gap between technical implementation and strategic product vision, with hands-on
                expertise in Agile development, API ecosystems, and stakeholder communication.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Core Competencies Section */}
      <Box sx={{ py: 10, px: 3, backgroundColor: "white" }}>
        <Box sx={{ maxWidth: "1200px", margin: "0 auto" }}>
          <Box display="flex" alignItems="center" justifyContent="center" mb={6}>
            <ManageAccountsIcon sx={{ fontSize: 40, color: "#10b981", mr: 2 }} />
            <Typography
              variant="h3"
              sx={{
                fontFamily: "Roboto Slab",
                fontWeight: 700,
                textAlign: "center"
              }}
            >
              Core Competencies
            </Typography>
          </Box>

          <Card 
            sx={{ 
              borderRadius: 3, 
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
              maxWidth: "900px",
              margin: "0 auto"
            }}
          >
            <CardContent sx={{ p: 5 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                  gap: 3
                }}
              >
                {experienceData.coreCompetencies.map((competency, index) => (
                  <Box 
                    key={index} 
                    display="flex" 
                    alignItems="flex-start" 
                    gap={2}
                  >
                    <CheckCircleIcon 
                      sx={{ 
                        color: "#10b981", 
                        fontSize: "1.5rem",
                        mt: 0.5,
                        flexShrink: 0
                      }} 
                    />
                    <Typography
                      sx={{
                        fontFamily: "Roboto",
                        fontSize: "1.1rem",
                        color: "#333",
                        fontWeight: 500,
                        lineHeight: 1.6
                      }}
                    >
                      {competency}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Experience Section */}
      <Box sx={{ py: 10, px: 3, backgroundColor: "#f5f7fa" }}>
        <Box sx={{ maxWidth: "1200px", margin: "0 auto" }}>
          <Box display="flex" alignItems="center" justifyContent="center" mb={6}>
            <WorkIcon sx={{ fontSize: 40, color: "#10b981", mr: 2 }} />
            <Typography
              variant="h3"
              sx={{
                fontFamily: "Roboto Slab",
                fontWeight: 700,
                textAlign: "center"
              }}
            >
              Professional Experience
            </Typography>
          </Box>

          {experienceData.experience.map((job, index) => (
            <JobBox key={index} job={job} />
          ))}
        </Box>
      </Box>

      {/* Education Section */}
      <Box sx={{ py: 10, px: 3, backgroundColor: "white" }}>
        <Box sx={{ maxWidth: "1200px", margin: "0 auto" }}>
          <Box display="flex" alignItems="center" justifyContent="center" mb={6}>
            <SchoolIcon sx={{ fontSize: 40, color: "#10b981", mr: 2 }} />
            <Typography
              variant="h3"
              sx={{
                fontFamily: "Roboto Slab",
                fontWeight: 700,
                textAlign: "center"
              }}
            >
              Education
            </Typography>
          </Box>

          {experienceData.education.map((edu, index) => (
            <EducationBox key={index} edu={edu} />
          ))}
        </Box>
      </Box>

      {/* Certifications Section */}
      <Box sx={{ py: 10, px: 3, backgroundColor: "#f5f7fa" }}>
        <Box sx={{ maxWidth: "1200px", margin: "0 auto" }}>
          <Box display="flex" alignItems="center" justifyContent="center" mb={6}>
            <VerifiedIcon sx={{ fontSize: 40, color: "#10b981", mr: 2 }} />
            <Typography
              variant="h3"
              sx={{
                fontFamily: "Roboto Slab",
                fontWeight: 700,
                textAlign: "center"
              }}
            >
              Certifications
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
              gap: 3
            }}
          >
            {experienceData.certifications.map((cert, index) => (
              <Card
                key={index}
                sx={{
                  borderRadius: 3,
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)"
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: "Roboto Slab",
                      fontWeight: 600,
                      color: "#333",
                      mb: 1
                    }}
                  >
                    {cert.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "Roboto",
                      color: "#10b981",
                      fontWeight: 600,
                      mb: 2
                    }}
                  >
                    {cert.institution}
                  </Typography>
                  <Link
                    href={cert.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      fontFamily: "Roboto",
                      color: "#10b981",
                      textDecoration: "none",
                      fontWeight: 600,
                      display: "inline-flex",
                      alignItems: "center",
                      "&:hover": {
                        textDecoration: "underline"
                      }
                    }}
                  >
                    View Certificate →
                  </Link>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default Experience;
